<template>
  <div
    class="AtlasWidgetMain"
    :class="{
      'is-loading': isLoading,
      'is-fullscreen': isFullscreen,
      'is-displayingTooltipMessage':
        isDisplayingTooltipMessage || showTooMuchData,
      [`AtlasWidgetMain--${modifier}`]: true,
      'AtlasWidgetMain__figure-caption': showFigureCaption,
    }"
  >
    <atlas-figure-caption
      v-if="showFigureCaption"
      :breakdown-id="activeBreakdown"
      class="AtlasWidgetMain-figcaption"
    />
    <div
      class="AtlasWidgetMain-inner"
      :class="{ 'AtlasWidgetMain-inner__figure-caption': showFigureCaption }"
    >
      <header
        class="AtlasWidgetMain-header"
        :class="{
          'AtlasWidgetMain-header__figure-caption': showFigureCaption,
        }"
      >
        <atlas-widget-header
          :indicator-config="indicatorConfig"
          :breakdownId="activeBreakdown"
          :skin="skin"
        />

        <atlas-map-legend
          v-if="breakdown.type === 'map' && !breakdown?.hideLegend"
          :scheme="breakdown.scheme"
          :format-string="breakdown.legendFormat"
          :prefix="breakdown.legendPrefix"
          :suffix="breakdown.legendSuffix"
        />

        <atlas-widget-legend
          v-else-if="hasLegend && !hideLegend"
          v-bind="breakdown.legend"
          :transforms="transforms"
          :data="data"
          :sorts="breakdown.legendSorter ?? breakdown.sorts"
        />
      </header>

      <nav
        v-if="displayControls"
        class="AtlasWidgetMain-menu"
        :class="{
          'AtlasWidgetMain-menu--floatAbove': breakdown.type === 'map',
        }"
      >
        <atlas-breakdown-controls
          :is-loading="isLoading"
          :breakdown-id="activeBreakdown"
          @setActiveBreakdown="setActiveBreakdown"
          :disabledDownloadImage="disabledDownloadImage"
          :disabledDownloadPowerpoint="disabledDownloadPowerpoint"
          :disabledDownloadXlsx="disabledDownloadXlsx"
        />
      </nav>

      <main
        class="AtlasWidgetMain-content"
        :class="{
          'AtlasWidgetMain-content--noLegend':
            !hasLegend && breakdown.type !== 'map',
        }"
        ref="content"
        @mousemove="onMousemove"
        @mouseleave="onDatumMouseout"
      >
        <atlas-alert
          v-if="errorMsg"
          :message="errorMsg"
          :status="errorStatus"
          :is-snackbar="hasLoaded"
          @dismiss="dismissError"
        />

        <div
          v-if="breakdown.type !== 'map' && isBreakdownLoading && !data.length"
          :style="{ width: `${chartWidth}px`, height: `${chartHeight}px` }"
        >
          <atlas-loader />
        </div>

        <div
          v-else-if="hasLoaded"
          class="AtlasWidgetMain-visualization"
          :class="{
            [`AtlasWidgetMain-visualization--${breakdown.type}`]: true,
            'is-hasTabs': hasFooterTabs,
          }"
          ref="atlasVisualization"
        >
          <div
            class="AtlasWidgetMain-map__container"
            v-if="breakdown.type === 'map'"
          >
            <atlas-map-tilesetter
              :breakdown-id="activeBreakdown"
              :height="chartHeight"
              :complexity="geoComplexity"
              :args="args"
              @datum:mouseover="onDatumMouseover"
              @datum:mouseout="onDatumMouseout"
              @datum:toomuchdata="onDatumTooMuchData"
            />
          </div>

          <div v-else-if="data.length" class="AtlasWidgetMain-chart__container">
            <datalas-chart
              class="AtlasWidgetMain-chart"
              :config="indicatorConfig"
              :width="chartWidth"
              :height="chartHeight"
              :display="breakdown"
              :data="data"
              :transforms="transforms"
              :key-prefix="renderKey"
              @datum:mouseover="onDatumMouseover"
              @datum:mouseout="onDatumMouseout"
            />
          </div>

          <div v-else class="AtlasWidgetMain-empty">
            <div
              v-if="breakdown.noResultsMessage"
              v-html="sanitize(breakdown.noResultsMessage)"
            ></div>
            <template v-else>
              <p>
                We're sorry, but data is not available for this indicator and/or
                geography.
              </p>
              <p>
                Please try searching for a larger geography (e.g. a region,
                county, or sub-county area instead of a city) or changing the
                breakdown and/or filter settings above.
              </p>
            </template>
          </div>

          <div class="AtlasWidgetMain-datumExplorerContainer">
            <transition name="fade">
              <atlas-datum-explorer
                ref="datumExplorer"
                class="AtlasWidgetMain-datumExplorer"
                v-if="exploreDatum"
                :datum="exploreDatum"
                :breakdown-id="activeBreakdown"
                :style="{
                  top: `${datumExplorerPosition.top}px`,
                  left: `${datumExplorerPosition.left}px`,
                }"
              />
            </transition>

            <transition name="fade">
              <atlas-datum-explorer
                class="AtlasWidgetMain-datumExplorer"
                v-if="showTooMuchData"
                :datum="showTooMuchData"
              />
            </transition>
          </div>
        </div>

        <div class="AtlasWidgetMain-footer" ref="footer">
          <div
            class="AtlasWidgetMain-source"
            :class="
              hasFooterTabs
                ? 'AtlasWidgetMain-source--right'
                : 'AtlasWidgetMain-source--left'
            "
            v-if="breakdown.dataSource"
          >
            Data source: {{ breakdown.dataSource }}
          </div>
          <atlas-widget-tabs
            v-if="hasLoaded && hasFooterTabs"
            v-model:tab-height="tabHeight"
            :breakdown-id="activeBreakdown"
          />
        </div>
      </main>
      <div ref="globalFooter">
        <atlas-footer v-if="displayGlobalFooter" />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import sanitize from 'sanitize-html';

import { normalizeError } from '@datalas/common';

import { decipherYear } from '../../lib/api';

import AtlasFigureCaption from './figure-caption.vue';
import AtlasWidgetHeader from './header.vue';
import AtlasBreakdownControls from './breakdown-controls.vue';
import AtlasWidgetLegend from './legend.vue';
import AtlasDatumExplorer from './datum-explorer.vue';
import AtlasMapLegend from './map-legend.vue';
import AtlasWidgetTabs from './tabs.vue';
import AtlasMapTilesetter from './map-tilesetter.vue';

import breakdownCaption from '../../utils/breakdownCaption';
import { SKIN_KEYS } from '../../constants/index';

export default defineComponent({
  name: 'atlas-widget-main',

  inheritAttrs: false,

  emits: ['load', 'breakdown:updated', 'set-default-geo-compare-state'],

  inject: ['widgetContext'],

  components: {
    AtlasFigureCaption,
    AtlasWidgetHeader,
    AtlasBreakdownControls,
    AtlasWidgetLegend,
    AtlasDatumExplorer,
    AtlasMapLegend,
    AtlasWidgetTabs,
    AtlasMapTilesetter,
  },

  props: {
    breakdownId: {
      type: String,
    },

    args: {
      type: Object,
      default: () => ({}),
    },

    geoCodeA: {
      type: String,
    },

    geoCodeB: {
      type: String,
    },

    geoType: {
      type: String,
    },

    geoComplexity: {
      type: String,
    },

    modifier: {
      type: String,
      default: 'app',
    },

    dataVersion: {
      type: String,
    },

    dataVersionOverride: {
      type: String,
    },

    tenant: {
      type: String,
    },

    displayGroupRevision: {
      type: Number,
    },

    disabledDownloadImage: { type: Boolean },
    disabledDownloadPowerpoint: { type: Boolean },
    disabledDownloadXlsx: { type: Boolean },
  },

  data: () => ({
    errorMsg: null,
    errorStatus: null,
    exploreDatum: null,
    tabHeight: 0,
    scaleRatio: 1,
    totalChartHeight: null,
    chartWidth: window.innerWidth,
    isDisplayingTooltipMessage: false,
    showTooMuchData: null,
    clientX: 0,
    clientY: 0,
    atlasVisualizationBoundingRect: {},
    datumExplorerBoundingRect: {},
  }),

  computed: {
    ...mapState('ui', ['isFullscreen']),

    ...mapState('indicator', ['indicatorId', 'displayRev']),

    ...mapState('breakdown', [
      'isDownloadLoading',
      'hasLoaded',
      'primaryGeo',
      'secondaryGeo',
    ]),

    ...mapState('breakdown', {
      activeGeoType: 'geoType',
      isBreakdownLoading: 'isLoading',
      geos: 'geos',
    }),

    ...mapGetters('display', [
      'displayControls',
      'displayFigureCaption',
      'displayFooterTabs',
      'displayGlobalFooter',
      'displayBreakdownSelect',
    ]),

    ...mapGetters('indicator', [
      'indicatorConfig',
      'breakdownsById',
      'defaultBreakdown',
    ]),

    ...mapGetters('breakdown', [
      'selectedBreakdownId',
      'breakdownById',
      'filtersByBreakdownId',
      'tokenContextByBreakdownId',
      'dataByBreakdownId',
      'contextByBreakdownId',
      'transformsByBreakdownId',
    ]),
    ...mapGetters('breakdown/query', ['breakdownSlug']),

    breakdown() {
      return this.breakdownById(this.activeBreakdown);
    },
    filters() {
      return this.filtersByBreakdownId(this.activeBreakdown);
    },
    data() {
      return this.dataByBreakdownId(this.activeBreakdown);
    },

    context() {
      return this.contextByBreakdownId(this.activeBreakdown);
    },
    transforms() {
      return this.transformsByBreakdownId(this.activeBreakdown);
    },

    isLoading() {
      return this.isBreakdownLoading || this.isDownloadLoading;
    },

    chartHeight() {
      return this.breakdown.type !== 'map'
        ? Math.max(this.totalChartHeight - this.tabHeight * this.scaleRatio, 0)
        : Math.max(this.totalChartHeight - this.tabHeight, 0);
    },

    hasLegend() {
      return Boolean(this.breakdown.legend && this.data.length);
    },

    hideLegend() {
      return Boolean(this.breakdown?.hideLegend);
    },

    skin() {
      return this.widgetContext.value.skin || this.indicatorConfig?.skin;
    },

    showFigureCaption() {
      return this.skin === SKIN_KEYS.DATA_SUMMARY && this.displayFigureCaption;
    },

    hasFooterTabs() {
      const { displayFooterTabs, caption } = this;
      return Boolean(displayFooterTabs && caption && !this.showFigureCaption);
    },

    renderKey() {
      return (
        this.breakdown.contexts
          .filter((context) => context.reRender)
          .map(({ type }) => `${type}:${this.context[type]}`)
          .join('') || ''
      );
    },

    activeBreakdown() {
      return this.selectedBreakdownId || this.breakdownId;
    },

    queryKey() {
      const params = {
        breakdownId: this.activeBreakdown,
        geoCodeA: this.geoCodeA,
        geoType: this.geoType,
        dataVersionOverride: this.dataVersionOverride,
        tenant: this.tenant,
      };

      const breakdown =
        this.breakdownsById[this.activeBreakdown] || this.defaultBreakdown;
      if (breakdown && breakdown.config.allowGeoCompare) {
        params.geoCodeB = this.geoCodeB;
      }

      return JSON.stringify(params);
    },

    datumExplorerPosition() {
      const offset = 20;
      const position = { top: 0, left: 0 };

      if (
        (this.clientY + this.datumExplorerBoundingRect.height || 0) + offset >
        this.atlasVisualizationBoundingRect.height +
          this.atlasVisualizationBoundingRect.top
      ) {
        position.top =
          this.clientY -
          this.atlasVisualizationBoundingRect.top -
          this.datumExplorerBoundingRect?.height;
      } else {
        position.top =
          this.clientY - this.atlasVisualizationBoundingRect.top + offset;
      }

      if (
        (this.clientX + this.datumExplorerBoundingRect?.width || 0) + offset >
        this.atlasVisualizationBoundingRect?.width +
          this.atlasVisualizationBoundingRect.left
      ) {
        position.left =
          this.clientX -
          this.atlasVisualizationBoundingRect.left -
          this.datumExplorerBoundingRect?.width;
      } else {
        position.left =
          this.clientX - this.atlasVisualizationBoundingRect.left + offset;
      }

      return position;
    },

    tokenContext() {
      return this.tokenContextByBreakdownId(this.activeBreakdown);
    },
    caption() {
      if (!this.hasLoaded) return '';

      return breakdownCaption(
        this.breakdown,
        this.data,
        this.tokenContext,
        this.secondaryGeo,
      );
    },
  },

  watch: {
    queryKey: {
      immediate: true,
      handler(value, oldValue) {
        if (value !== oldValue) this.fetchData();
      },
    },

    args(args) {
      if (!this.isBreakdownLoading) {
        this.$store.commit('breakdown/setArgs', { args });
      }
    },

    isFullscreen() {
      this.$nextTick(() => {
        this.onResize();
      });
    },

    breakdown: {
      immediate: true,
      handler() {
        this.checkIfBreakdownHasDefaultCompareAndEmit();
      },
    },

    breakdownId: {
      immediate: true,
      handler(breakdownId) {
        if (breakdownId && breakdownId !== this.activeBreakdown) {
          this.$router.push({
            ...this.$route,
            query: { ...this.$route.query, breakdown: breakdownId },
          });
          this.selectBreakdown(breakdownId);
        }
      },
    },

    displayGroupRevision: {
      immediate: true,
      handler(revision) {
        const queryBreakdown = this.activeBreakdown;

        const breakdown = queryBreakdown
          ? this.breakdownsById[queryBreakdown]
          : this.defaultBreakdown;

        const defaultGeoCompare =
          breakdown.config?.allowGeoCompare &&
          breakdown.config?.geoCodes.length > 1;

        if (defaultGeoCompare) {
          // Used if defaultGeoCompare is enabled to check
          // if the breakdown has 2 geo codes in its config
          const computedGeoCodeB = computed(() => {
            if (typeof this.geoCodeB === 'object') {
              this.$emit('set-default-geo-compare-state', defaultGeoCompare);
              return `${breakdown.config.geoCodes[1]}`;
            }

            return null;
          });

          this.onSecondaryGeoUpdate(computedGeoCodeB.value);
        }
      },
    },
  },

  methods: {
    ...mapActions({
      selectBreakdown: 'breakdown/selectBreakdown',
    }),

    ...mapActions({
      fetchIndicatorGeoSelectorOptions:
        'indicator/fetchIndicatorGeoSelectorOptions',
    }),

    sanitize,

    dismissError() {
      this.errorMsg = null;
      this.errorStatus = null;
    },

    pushQueryToRouter(value, key) {
      return this.$router.push({
        ...this.$route,
        query: { ...this.$route.query, [key]: value },
      });
    },

    async onSecondaryGeoUpdate(geoCode) {
      if (geoCode) {
        await this.fetchIndicatorGeoSelectorOptions({
          id: this.indicatorId,
          displayRev: this.displayRev,
          geoCode,
        });

        await this.pushQueryToRouter(geoCode, 'geo_compare');
      }
    },

    initBoundingsRect() {
      this.$nextTick(() => {
        if (this.exploreDatum && this.$refs.atlasVisualization) {
          this.atlasVisualizationBoundingRect = this.$refs.atlasVisualization.getBoundingClientRect();
        }
        if (this.exploreDatum && this.$refs.datumExplorer) {
          this.datumExplorerBoundingRect = this.$refs.datumExplorer.$el.getBoundingClientRect();
        }
      });
    },

    clearBoundingsRect() {
      if (!this.exploreDatum) {
        this.atlasVisualizationBoundingRect = {};
        this.datumExplorerBoundingRect = {};
      }
    },

    onDatumMouseover(datum) {
      if (!this.breakdown.statements.MOUSEOVER || !datum) {
        this.resetExploreDatum();
        return;
      }
      if (datum.year) {
        // gitlab issue #198
        // eslint-disable-next-line no-param-reassign
        datum.year = decipherYear(datum.year, this.indicatorId < 191);
      }
      this.exploreDatum = datum;
      this.isDisplayingTooltipMessage = Boolean(datum.isMessage);

      this.initBoundingsRect();
    },

    onMousemove(event) {
      this.clientX = event.clientX;
      this.clientY = event.clientY;
    },

    onDatumMouseout() {
      this.resetExploreDatum();
    },

    resetExploreDatum() {
      this.exploreDatum = null;
      this.isDisplayingTooltipMessage = false;

      this.clearBoundingsRect();
    },

    onDatumTooMuchData(tooMuchData) {
      this.showTooMuchData = tooMuchData
        ? {
            message:
              'Too many data points. Select a smaller geographical area to see all data.',
          }
        : null;
    },

    checkIfBreakdownHasDefaultCompareAndEmit() {
      const breakdownHasSecondaryGeoCode =
        this.breakdown?.allowGeoCompare && this.breakdown?.geoCodes?.length > 1;

      this.$emit('set-default-geo-compare-state', breakdownHasSecondaryGeoCode);
    },

    async fetchData() {
      const queryBreakdown = this.activeBreakdown;

      const breakdown = queryBreakdown
        ? this.breakdownsById[queryBreakdown]
        : this.defaultBreakdown;

      // If we couldn't find breakdown options the id must be wrong
      if (!breakdown) {
        this.errorMsg = 'Invalid Breakdown ID';
        this.errorStatus = null;

        this.$emit('load');

        return;
      }

      const defaultGeoCompare =
        breakdown.config?.allowGeoCompare &&
        breakdown.config?.geoCodes.length > 1;

      // Used if defaultGeoCompare is enabled to check
      // if the breakdown has 2 geo codes in its config
      let computedGeoCodeB = null;

      if (
        defaultGeoCompare &&
        this.$router.currentRoute.value.query.breakdown === breakdown.slug
      ) {
        computedGeoCodeB = computed(() => {
          if (typeof this.geoCodeB === 'object') {
            this.$emit('set-default-geo-compare-state', defaultGeoCompare);
            return `${breakdown.config.geoCodes[1]}`;
          }

          return null;
        });

        this.onSecondaryGeoUpdate(computedGeoCodeB.value);
      }

      this.errorMsg = null;
      this.errorStatus = null;

      const payload = {
        indicatorId: this.indicatorId,
        breakdown,
        primaryGeoCode: this.geoCodeA,
        secondaryGeoCode: computedGeoCodeB?.value ?? this.geoCodeB,
        geoType: this.geoType,
        args: this.args,
        dataVersion: this.dataVersionOverride,
        datasetName: this.dataVersion,
        displayRev: this.displayRev,
      };

      try {
        await this.$store.dispatch('breakdown/setBreakdown', payload);
      } catch (error) {
        const { status, text } = normalizeError(error);
        this.errorStatus = status;
        this.errorMsg = text;
      }

      this.$emit('load');

      if (!this.errorMsg) {
        this.exploreDatum = null;
      }

      // Recalculate dimensions after everthing renders
      this.$nextTick(() => {
        this.onResize();
      });
    },

    setScaleRatio() {
      this.scaleRatio = this.chartWidth / document.body.offsetWidth;
    },

    setChartWidth() {
      this.chartWidth = this.isFullscreen
        ? window.innerWidth
        : this.indicatorConfig.width;
    },

    setTotalChartHeight() {
      if (this.isFullscreen && this.$refs.content) {
        const { footer, globalFooter, content } = this.$refs;
        const display = content.firstElementChild || content;

        // Get the height of the area between the start of the visualization and
        // the bottom of the window, minus the global footer
        this.totalChartHeight = window.innerHeight;
        this.totalChartHeight -= display.getBoundingClientRect().top;
        this.totalChartHeight -= globalFooter.getBoundingClientRect().height;

        // Subtract the tabs/data source height/margin for non-maps (on maps
        // they overlap the display)
        if (this.breakdown.type !== 'map') {
          const footerStyle = getComputedStyle(footer);
          this.totalChartHeight -= footer.getBoundingClientRect().height;
          this.totalChartHeight -= parseFloat(footerStyle.marginTop);
        }

        this.totalChartHeight = Math.max(this.totalChartHeight, 0);
      } else if (this.breakdown.height) {
        this.totalChartHeight = this.breakdown.height;
      } else {
        this.totalChartHeight = this.indicatorConfig.height;
      }
    },

    onResize() {
      this.setChartWidth();
      this.setScaleRatio();
      this.setTotalChartHeight();
    },

    setActiveBreakdown(breakdown) {
      if (breakdown) this.selectBreakdown(breakdown);

      this.$emit('breakdown:updated', breakdown);
    },
  },

  mounted() {
    this.onResize();

    window.addEventListener('resize', this.onResize);
  },

  unmounted() {
    window.removeEventListener('resize', this.onResize);
  },
});
</script>

<style lang="scss">
.AtlasWidgetMain {
  &--figure {
    @media screen and (min-width: 768px) {
      display: flex;
      flex: 1;
    }

    .AtlasWidgetMain {
      &-figcaption,
      &-inner {
        flex-basis: 0;
        flex-grow: 1;
      }

      &-inner {
        // 10px margin + 10px padding already on .AtlasHeader-context makes
        // 20px in designs.
        margin-top: 10px;
      }

      &-visualization,
      &-source {
        @include padding(null $gutter);
      }
    }
  }

  &-figcaption {
    width: 100%;
    padding-top: 20px;

    @media screen and (min-width: 768px) {
      margin-right: 2.12766%;
      width: 40.42553%;
    }
  }

  &__figure-caption {
    @media screen and (min-width: 1100px) {
      padding-left: 15px;
      padding-right: 15px;
    }
  }

  &-inner {
    width: 100%;
    height: 100%;

    &__figure-caption {
      @media screen and (min-width: 768px) {
        margin-right: 2.12766%;
        width: 48.93617%;
        margin-left: 8.51064%;
        margin-right: 0;
      }
    }
  }

  &.is-fullscreen {
    @include position(fixed, 0 0 null);
    z-index: 9999999;
    height: 100vh;
    background: #fff;
  }

  &-header {
    @include shadow-strip;
    z-index: z(header);
    background-color: color(bg);

    .AtlasWidgetMain--figure & {
      z-index: auto;
      background-color: transparent;

      &::after {
        display: none;
      }
    }
  }

  &-menu,
  &-content {
    transition: opacity 0.3s;

    .AtlasWidgetMain.is-loading & {
      opacity: 0.4;
      pointer-events: none;
    }
  }

  &-menu {
    position: relative;
    z-index: z(menu);
    width: 100%;
    margin: 0 0 30px;
    font-size: 16px;
    font-weight: bold;

    &--floatAbove {
      position: absolute;
      margin: 0;
    }
  }

  &-content {
    margin: 0 auto;

    &--noLegend {
      padding-top: 31px;
    }
  }

  &-visualization {
    position: relative;

    &--map {
      &.is-hasTabs {
        margin-bottom: -$tab-height - $footer-margin;
      }
    }
  }

  &-chart {
    display: block;
    height: auto;
  }

  &-empty {
    max-width: 600px;
    padding: 75px 20px;
    margin: 0 auto;
    line-height: 1.6;
    text-align: center;
  }

  &-datumExplorerContainer {
    // @include position(absolute, 50% $gutter null null);
    position: absolute;
    z-index: z(explorer);
    transform: translateY(-50%);
    top: 0;
  }

  &-datumExplorer {
    position: absolute;

    &.fade-enter-active,
    &.fade-leave-active {
      transition: opacity 0.3s;
    }

    &.fade-enter-from,
    &.fade-leave-to {
      opacity: 0;
    }

    // When the tooltip is displaying a persistent message, put it behind the
    // filters so it doesn't block them. When it's a mouseover it goes in front.
    .AtlasWidgetMain.is-displayingTooltipMessage & {
      z-index: z(menu) - 1;
    }
  }

  &-footer {
    margin-top: $footer-margin;
    position: relative;
  }

  &-source {
    padding: 0 0.5em 15px;
    background-color: rgba(color(white), 0.8);
    color: color(disabled);
    font-size: 13px;
    line-height: 1.5;

    &--right {
      z-index: 1;
      padding-bottom: 0;
      flex-direction: row;
      position: absolute;
      top: 12px;
      right: calc(15px - 0.5em);
    }
  }
}

.AtlasWidgetMain {
  &__figure-caption {
    display: flex;
    flex-direction: column;

    @media screen and (min-width: 768px) {
      flex-direction: row;
    }
  }

  &-header__figure-caption {
    background: none;
  }

  &-header__figure-caption::after {
    height: 0;
  }
}
</style>
